import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Save, CheckCircle2, Printer, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import GSTAccessGate, { getGstToken, clearGstToken } from '../components/GSTAccessGate';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const nzd = (v) => `$${Number(v || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Default period: the most recent COMPLETED two-month period ending in an odd month (Jan/Mar/May/Jul/Sep/Nov)
const defaultPeriodEnd = (months) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1); // last completed month
  if (months === 2 && d.getMonth() % 2 === 1) d.setMonth(d.getMonth() - 1); // getMonth 0=Jan → odd index = even month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const BOX_LABELS = {
  5: 'Total sales and income for the period (including GST and any zero-rated supplies)',
  6: 'Zero-rated supplies included in Box 5',
  7: 'Subtract Box 6 from Box 5',
  8: 'Multiply Box 7 by 3 and divide by 23 — GST on sales',
  9: 'Adjustments from your calculation sheet (debit adjustments)',
  10: 'Add Box 8 and Box 9 — total GST collected on sales and income',
  11: 'Total purchases and expenses (including GST) for which tax invoicing requirements have been met, excluding imported goods',
  12: 'Multiply Box 11 by 3 and divide by 23 — GST on purchases and expenses',
  13: 'Credit adjustments from your calculation sheet',
  14: 'Add Box 12 and Box 13 — total GST credit for purchases and expenses',
  15: 'Difference between Box 10 and Box 14 — GST to pay or GST refund',
};
const MANUAL = { 6: 'zero_rated', 9: 'debit_adjustments', 13: 'credit_adjustments' };

export default function GSTReturn() {
  return <GSTAccessGate><GSTReturnInner /></GSTAccessGate>;
}

function GSTReturnInner() {
  const { getAuthHeader, hasPermission } = useAuth();
  const { stores, selectedStore } = useStore();
  const [store, setStore] = useState(selectedStore);
  const [months, setMonths] = useState(2);
  const [periodEnd, setPeriodEnd] = useState(() => defaultPeriodEnd(2));
  const [basis, setBasis] = useState('payments');
  const [data, setData] = useState(null);
  const [manual, setManual] = useState({ zero_rated: 0, debit_adjustments: 0, credit_adjustments: 0, other_expenses: 0 });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const canSee = hasPermission('admin');

  const load = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/gst/return`, { params: { store, period_end: periodEnd, months, basis }, headers: { ...getAuthHeader(), 'X-GST-Token': getGstToken() } });
      setData(r.data);
      setManual(r.data.manual);
      setNotes(r.data.notes || '');
      if (r.data.saved_basis && r.data.saved_basis !== basis) setBasis(r.data.saved_basis);
    } catch (e) {
      if (e?.response?.status === 423) { clearGstToken(); window.location.reload(); return; }
      toast.error(e?.response?.data?.detail || 'Could not load GST figures');
    } finally {
      setLoading(false);
    }
  }, [store, periodEnd, months, basis, canSee, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  // Live recalculation while typing manual boxes — same formula as the server
  const num = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;
  const gstOf = (incl) => Math.round(incl * 3 / 23 * 100) / 100;
  const src = data?.sources;
  const box5 = src ? num(src.pos_sales.amount - src.refunds.amount + src.customer_invoices.amount) : 0;
  const box6 = num(manual.zero_rated);
  const box7 = num(box5 - box6);
  const box8 = gstOf(box7);
  const box9 = num(manual.debit_adjustments);
  const box10 = num(box8 + box9);
  const box11 = src ? num(src.supplier_invoices.amount + num(manual.other_expenses)) : 0;
  const box12 = gstOf(box11);
  const box13 = num(manual.credit_adjustments);
  const box14 = num(box12 + box13);
  const box15 = num(box10 - box14);
  const boxes = { 5: box5, 6: box6, 7: box7, 8: box8, 9: box9, 10: box10, 11: box11, 12: box12, 13: box13, 14: box14, 15: box15 };

  const save = async (status) => {
    if (status === 'filed' && !window.confirm(`Mark this ${data.store_name} GST return (${data.period_label}) as FILED?\n\nBox 15: ${nzd(Math.abs(box15))} ${box15 >= 0 ? 'to pay' : 'refund'}\n\nThe figures are frozen for your records.`)) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/gst/return`, { store, period_end: periodEnd, months, basis, ...manual, notes, status }, { headers: { ...getAuthHeader(), 'X-GST-Token': getGstToken() } });
      setData(r.data);
      toast.success(status === 'filed' ? 'GST return marked as filed' : 'Draft saved');
    } catch (e) {
      if (e?.response?.status === 423) { clearGstToken(); window.location.reload(); return; }
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!canSee) {
    return <div className="p-8 text-slate-600" data-testid="gst-no-access">GST returns are only available to the owner / admin.</div>;
  }

  const filed = data?.status === 'filed';
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="gst-return-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase flex items-center gap-2"><Landmark className="w-6 h-6" /> GST Return</h1>
          <p className="text-sm text-slate-500">IRD GST101A figures from your POS — one return per company (store). Copy the boxes into myIR.</p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="gst-print"><Printer className="w-4 h-4 mr-2" /> Print</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white border rounded-lg p-4 print:hidden">
        <label className="text-xs font-medium text-slate-600">Company / store
          <select className="mt-1 w-full border rounded-md px-2 py-2 text-sm" value={store} onChange={e => setStore(e.target.value)} data-testid="gst-store">
            {stores.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">Filing frequency
          <select className="mt-1 w-full border rounded-md px-2 py-2 text-sm" value={months} onChange={e => { const m = Number(e.target.value); setMonths(m); setPeriodEnd(defaultPeriodEnd(m)); }} data-testid="gst-months">
            <option value={1}>Monthly</option><option value={2}>Two-monthly</option><option value={6}>Six-monthly</option>
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">Period ends (last month)
          <Input type="month" className="mt-1" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} data-testid="gst-period-end" />
        </label>
        <label className="text-xs font-medium text-slate-600">Accounting basis
          <select className="mt-1 w-full border rounded-md px-2 py-2 text-sm" value={basis} onChange={e => setBasis(e.target.value)} disabled={filed} data-testid="gst-basis">
            <option value="payments">Payments (cash) basis</option><option value="invoice">Invoice basis</option>
          </select>
        </label>
      </div>

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-semibold text-slate-800" data-testid="gst-period-label">{data.store_name} · {data.period_label}</span>
            <span className="text-slate-500">{basis === 'payments' ? 'Payments basis — counted when money changed hands' : 'Invoice basis — counted on invoice date'}</span>
            {filed && <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="gst-filed-badge"><CheckCircle2 className="w-4 h-4" /> Filed {new Date(data.filed_at).toLocaleDateString('en-NZ')} by {data.filed_by}</span>}
          </div>

          <div className="bg-white border rounded-lg overflow-hidden" data-testid="gst-boxes">
            {Object.keys(boxes).map(k => {
              const n = Number(k);
              const manualKey = MANUAL[n];
              const isResult = n === 15;
              const isTotal = [7, 8, 10, 12, 14].includes(n);
              return (
                <div key={n} className={`flex items-center gap-4 px-4 py-3 border-b last:border-b-0 ${isResult ? 'bg-slate-900 text-white' : isTotal ? 'bg-slate-50' : ''}`} data-testid={`gst-box-${n}`}>
                  <div className={`w-14 shrink-0 font-heading font-bold text-lg ${isResult ? 'text-white' : 'text-slate-900'}`}>Box {n}</div>
                  <div className={`flex-1 text-sm ${isResult ? 'text-slate-200' : 'text-slate-600'}`}>{BOX_LABELS[n]}</div>
                  <div className="w-44 text-right">
                    {manualKey && !filed ? (
                      <Input type="number" step="0.01" className="text-right font-mono" value={manual[manualKey]} onChange={e => setManual({ ...manual, [manualKey]: e.target.value })} data-testid={`gst-input-${manualKey}`} />
                    ) : (
                      <span className={`font-mono text-base ${isResult ? 'font-bold text-xl' : 'font-semibold'}`} data-testid={`gst-value-${n}`}>
                        {isResult && box15 < 0 ? `(${nzd(-box15)})` : nzd(boxes[n])}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${box15 > 0 ? 'bg-amber-50 border-amber-200 text-amber-900' : box15 < 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-slate-50 text-slate-700'}`} data-testid="gst-result">
            {box15 > 0 ? `GST to pay IRD: ${nzd(box15)}` : box15 < 0 ? `GST refund due from IRD: ${nzd(-box15)}` : 'Nil return — nothing to pay or refund'}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Sources title="Where Box 5 comes from" rows={[
              [`POS sales (${src.pos_sales.count})${src.pos_sales.online_count ? ` incl. ${src.pos_sales.online_count} online orders ${nzd(src.pos_sales.online_amount)}` : ''}`, src.pos_sales.amount],
              [`Refunds given (${src.refunds.count})`, -src.refunds.amount],
              [`Customer invoices (${src.customer_invoices.count})`, src.customer_invoices.amount],
            ]} total={box5} detail={src.customer_invoices.rows} testId="gst-sources-sales" />
            <Sources title="Where Box 11 comes from" rows={[
              [`Supplier invoices received (${src.supplier_invoices.count})`, src.supplier_invoices.amount],
              ['Other expenses incl. GST (rent, power, etc.)', num(manual.other_expenses), !filed && (
                <Input type="number" step="0.01" min="0" className="w-36 text-right font-mono" value={manual.other_expenses} onChange={e => setManual({ ...manual, other_expenses: e.target.value })} data-testid="gst-input-other_expenses" />
              )],
            ]} total={box11} detail={src.supplier_invoices.rows} testId="gst-sources-purchases" />
          </div>

          <div className="flex items-start gap-2 text-xs text-slate-500">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>Figures use the IRD formula (GST content = amount × 3 ÷ 23). Supplier invoice amounts are taken as entered at receipting (GST-inclusive invoice total). Check Box 11 against your supplier invoices before filing — anything not receipted into the POS must go in "Other expenses".</p>
          </div>

          <div className="bg-white border rounded-lg p-4 space-y-3 print:hidden">
            <label className="text-xs font-medium text-slate-600 block">Notes for this return
              <textarea className="mt-1 w-full border rounded-md px-3 py-2 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} disabled={filed} data-testid="gst-notes" />
            </label>
            <div className="flex flex-wrap gap-3 justify-end">
              {filed ? (
                <Button variant="outline" onClick={() => save('draft')} disabled={saving} data-testid="gst-reopen">Re-open as draft</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => save('draft')} disabled={saving || loading} data-testid="gst-save-draft"><Save className="w-4 h-4 mr-2" /> Save draft</Button>
                  <Button onClick={() => save('filed')} disabled={saving || loading} className="bg-slate-900 hover:bg-slate-800" data-testid="gst-mark-filed"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as filed</Button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Sources = ({ title, rows, total, detail, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border rounded-lg p-4 text-sm" data-testid={testId}>
      <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
      {rows.map(([label, amount, control], i) => (
        <div key={i} className="flex items-center justify-between py-1 gap-3">
          <span className="text-slate-600">{label}</span>
          {control || <span className="font-mono">{amount < 0 ? `(${nzd(-amount)})` : nzd(amount)}</span>}
        </div>
      ))}
      <div className="flex justify-between border-t mt-2 pt-2 font-semibold"><span>Total</span><span className="font-mono">{nzd(total)}</span></div>
      {detail?.length > 0 && (
        <>
          <button type="button" className="text-xs text-[#1B5E20] underline mt-2" onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'} {detail.length} invoice{detail.length === 1 ? '' : 's'} included</button>
          {open && (
            <div className="mt-2 max-h-56 overflow-y-auto divide-y text-xs">
              {detail.map((r, i) => (
                <div key={i} className="flex justify-between py-1 gap-2"><span className="text-slate-500 w-20">{r.date}</span><span className="flex-1 truncate">{r.who}{r.ref ? ` · #${r.ref}` : ''}</span><span className="font-mono">{nzd(r.amount)}</span></div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
